import { Option } from "@/types";

/**
 * Hierarchical category structure for WoW item filtering
 * Categories with subcategories allow granular selection (e.g., specific weapon types)
 * Categories without subcategories are selected as a whole
 */
export interface HierarchicalCategory {
  value: string;
  label: string;
  subcategories?: Option[];
}

/**
 * Hierarchical categories with Weapon and Armor having expandable subcategories
 * Values use "Parent:Subcategory" format for subcategories to enable easy parsing
 */
export const hierarchicalCategories: HierarchicalCategory[] = [
  {
    value: "Weapon",
    label: "Weapon",
    subcategories: [
      { value: "Weapon:One-Handed Axes", label: "One-Handed Axes" },
      { value: "Weapon:Two-Handed Axes", label: "Two-Handed Axes" },
      { value: "Weapon:Bows", label: "Bows" },
      { value: "Weapon:Guns", label: "Guns" },
      { value: "Weapon:One-Handed Maces", label: "One-Handed Maces" },
      { value: "Weapon:Two-Handed Maces", label: "Two-Handed Maces" },
      { value: "Weapon:Polearms", label: "Polearms" },
      { value: "Weapon:One-Handed Swords", label: "One-Handed Swords" },
      { value: "Weapon:Two-Handed Swords", label: "Two-Handed Swords" },
      { value: "Weapon:Warglaives", label: "Warglaives" },
      { value: "Weapon:Staves", label: "Staves" },
      { value: "Weapon:Fist Weapons", label: "Fist Weapons" },
      { value: "Weapon:Daggers", label: "Daggers" },
      { value: "Weapon:Crossbows", label: "Crossbows" },
      { value: "Weapon:Wands", label: "Wands" },
      { value: "Weapon:Fishing Poles", label: "Fishing Poles" },
      { value: "Weapon:Miscellaneous", label: "Miscellaneous" },
    ],
  },
  {
    value: "Armor",
    label: "Armor",
    subcategories: [
      { value: "Armor:Cloth", label: "Cloth" },
      { value: "Armor:Leather", label: "Leather" },
      { value: "Armor:Mail", label: "Mail" },
      { value: "Armor:Plate", label: "Plate" },
      { value: "Armor:Shields", label: "Shields" },
      { value: "Armor:Rings, Necks...", label: "Rings, Necks..." },
      { value: "Armor:Cosmetic", label: "Cosmetic" },
      { value: "Armor:Librams", label: "Librams" },
      { value: "Armor:Idols", label: "Idols" },
      { value: "Armor:Totems", label: "Totems" },
      { value: "Armor:Sigils", label: "Sigils" },
      { value: "Armor:Miscellaneous", label: "Miscellaneous" },
    ],
  },
  // Categories without subcategories - selected as a whole
  { value: "Housing", label: "Housing" },
  { value: "Recipe", label: "Recipe" },
  { value: "Profession", label: "Profession" },
  { value: "Toys", label: "Toys" },
  { value: "Mount", label: "Mount" },
  { value: "Companions", label: "Companions" },
  { value: "Pet", label: "Pet (All Expansions)" },
  { value: "Miscellaneous", label: "Miscellaneous" },
];

/**
 * Get all subcategory values for a parent category
 * Useful for "select all" operations
 */
export const getSubcategoryValues = (parentValue: string): string[] => {
  const category = hierarchicalCategories.find((c) => c.value === parentValue);
  if (!category?.subcategories) return [];
  return category.subcategories.map((s) => s.value + "");
};

/**
 * Parse a hierarchical value into parent and subcategory parts
 * e.g., "Weapon:Bows" -> { parent: "Weapon", subcategory: "Bows" }
 * e.g., "Housing" -> { parent: "Housing", subcategory: null }
 */
export const parseHierarchicalValue = (
  value: string
): { parent: string; subcategory: string | null } => {
  if (value.includes(":")) {
    const [parent, subcategory] = value.split(":");
    return { parent, subcategory };
  }
  return { parent: value, subcategory: null };
};

/**
 * Check if a category has subcategories
 */
export const hasSubcategories = (parentValue: string): boolean => {
  const category = hierarchicalCategories.find((c) => c.value === parentValue);
  return !!category?.subcategories && category.subcategories.length > 0;
};

/**
 * Convert flat selected values to Option[] format for compatibility
 * with existing filter system
 */
export const valuesToOptions = (values: string[]): Option[] => {
  return values.map((value) => {
    // Find the label
    for (const cat of hierarchicalCategories) {
      if (cat.value === value) {
        return { value, label: cat.label };
      }
      if (cat.subcategories) {
        const sub = cat.subcategories.find((s) => s.value === value);
        if (sub) {
          return { value: sub.value, label: sub.label };
        }
      }
    }
    return { value, label: value };
  });
};

/**
 * Convert Option[] to flat string[] values
 */
export const optionsToValues = (options: Option[]): string[] => {
  return options.map((opt) => String(opt.value));
};

/**
 * Get all selectable values (for "select all" / initial state)
 * - For categories WITH subcategories: returns all subcategory values
 * - For categories WITHOUT subcategories: returns the category value itself
 *
 * Use this to initialize Zustand state with all categories selected
 */
export const getAllSelectableValues = (): string[] => {
  const values: string[] = [];

  hierarchicalCategories.forEach((category) => {
    if (category.subcategories && category.subcategories.length > 0) {
      // Add all subcategory values
      category.subcategories.forEach((sub) => {
        values.push(String(sub.value));
      });
    } else {
      // Add the category value itself
      values.push(category.value);
    }
  });

  return values;
};

/**
 * Pre-computed array of all selectable values for use in Zustand initial state
 * This avoids calling getAllSelectableValues() on every store initialization
 */
export const allSelectableCategoryValues: string[] = getAllSelectableValues();